import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, Save, Gem, Diamond, Sparkles, Ruler, Award, Info } from "lucide-react";
import { AdminShell } from "@/components/admin/AdminShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api, formatINR } from "@/lib/api";
import { toast } from "sonner";
import { hasAdminSession } from "@/lib/session";

const uid = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `id-${Date.now()}-${Math.random().toString(16).slice(2)}`);

const emptySection = () => ({ sizes: [], grades: [], rates: {} });
const cellKey = (sid, gid) => `${sid}|${gid}`;

/** Editable panel for a single diamond type (natural or lab). */
function SectionEditor({ section, onChange, testPrefix }) {
  const { sizes, grades, rates } = section;

  const update = (patch) => onChange({ ...section, ...patch });

  // ---- Sizes ----
  const addSize = () =>
    update({ sizes: [...sizes, { id: uid(), label: "", carat_min: "", carat_max: "" }] });
  const setSize = (id, key, value) =>
    update({ sizes: sizes.map((s) => (s.id === id ? { ...s, [key]: value } : s)) });
  const removeSize = (id) => {
    const nextRates = { ...rates };
    grades.forEach((g) => delete nextRates[cellKey(id, g.id)]);
    update({ sizes: sizes.filter((s) => s.id !== id), rates: nextRates });
  };

  // ---- Grades ----
  const addGrade = () => update({ grades: [...grades, { id: uid(), label: "" }] });
  const setGrade = (id, value) =>
    update({ grades: grades.map((g) => (g.id === id ? { ...g, label: value } : g)) });
  const removeGrade = (id) => {
    const nextRates = { ...rates };
    sizes.forEach((s) => delete nextRates[cellKey(s.id, id)]);
    update({ grades: grades.filter((g) => g.id !== id), rates: nextRates });
  };

  // ---- Rates ----
  const setRate = (sid, gid, value) =>
    update({ rates: { ...rates, [cellKey(sid, gid)]: value } });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Sizes */}
        <Card className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Ruler className="h-4 w-4 text-[#8E6F33]" />
              <h3 className="font-medium">Sizes</h3>
            </div>
            <Button data-testid={`${testPrefix}-add-size`} size="sm" variant="secondary" onClick={addSize} className="gap-1">
              <Plus className="h-3.5 w-3.5" /> Add Size
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            Label it however you like — sieve (+1), MM (1.30mm) or a carat bracket. Carat range is optional (used as a reference).
          </p>
          <div className="space-y-2">
            {sizes.map((s) => (
              <div key={s.id} data-testid={`${testPrefix}-size-row`} className="grid grid-cols-[1fr_84px_84px_auto] gap-2 items-center">
                <Input
                  data-testid={`${testPrefix}-size-label`}
                  value={s.label}
                  placeholder="e.g. +1 / 1.30mm / 0.03–0.07ct"
                  onChange={(e) => setSize(s.id, "label", e.target.value)}
                />
                <Input type="number" value={s.carat_min ?? ""} placeholder="ct min" className="font-mono" onChange={(e) => setSize(s.id, "carat_min", e.target.value)} />
                <Input type="number" value={s.carat_max ?? ""} placeholder="ct max" className="font-mono" onChange={(e) => setSize(s.id, "carat_max", e.target.value)} />
                <Button size="icon" variant="ghost" onClick={() => removeSize(s.id)} aria-label="Delete size"><Trash2 className="h-4 w-4 text-destructive" /></Button>
              </div>
            ))}
            {sizes.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">No sizes yet. Add one to build the grid.</p>}
          </div>
        </Card>

        {/* Grades */}
        <Card className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Award className="h-4 w-4 text-[#8E6F33]" />
              <h3 className="font-medium">Grades</h3>
            </div>
            <Button data-testid={`${testPrefix}-add-grade`} size="sm" variant="secondary" onClick={addGrade} className="gap-1">
              <Plus className="h-3.5 w-3.5" /> Add Grade
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mb-3">Combined colour + clarity, e.g. VVS-EF, VS-GH, SI-IJ.</p>
          <div className="space-y-2">
            {grades.map((g) => (
              <div key={g.id} data-testid={`${testPrefix}-grade-row`} className="grid grid-cols-[1fr_auto] gap-2 items-center">
                <Input
                  data-testid={`${testPrefix}-grade-label`}
                  value={g.label}
                  placeholder="e.g. VVS-EF"
                  onChange={(e) => setGrade(g.id, e.target.value)}
                />
                <Button size="icon" variant="ghost" onClick={() => removeGrade(g.id)} aria-label="Delete grade"><Trash2 className="h-4 w-4 text-destructive" /></Button>
              </div>
            ))}
            {grades.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">No grades yet. Add one to build the grid.</p>}
          </div>
        </Card>
      </div>

      {/* Rate grid */}
      <Card className="p-5">
        <div className="flex items-center gap-2 mb-1">
          <Diamond className="h-4 w-4 text-[hsl(var(--primary))]" />
          <h3 className="font-medium">Rate Grid — price per carat (₹)</h3>
        </div>
        <p className="text-xs text-muted-foreground mb-4">Enter the per-carat rate for every Size × Grade combination.</p>
        {sizes.length === 0 || grades.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-[#F7F0E6] p-8 text-center text-sm text-muted-foreground">
            Add at least one Size and one Grade above to reveal the rate grid.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table data-testid={`${testPrefix}-rate-grid`} className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th className="sticky left-0 z-10 bg-[#F4EBDD] text-left p-2 border border-border font-medium min-w-[140px]">Size \ Grade</th>
                  {grades.map((g) => (
                    <th key={g.id} className="bg-[#F4EBDD] p-2 border border-border font-medium whitespace-nowrap min-w-[120px]">{g.label || <span className="text-muted-foreground italic">(unnamed)</span>}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sizes.map((s) => (
                  <tr key={s.id}>
                    <th className="sticky left-0 z-10 bg-[hsl(var(--card))] text-left p-2 border border-border font-medium whitespace-nowrap">{s.label || <span className="text-muted-foreground italic">(unnamed)</span>}</th>
                    {grades.map((g) => (
                      <td key={g.id} className="border border-border p-1">
                        <Input
                          data-testid={`${testPrefix}-cell`}
                          type="number"
                          value={rates[cellKey(s.id, g.id)] ?? ""}
                          placeholder="0"
                          className="font-mono h-9 border-0 focus-visible:ring-1"
                          onChange={(e) => setRate(s.id, g.id, e.target.value)}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

export default function DiamondRates() {
  const navigate = useNavigate();
  const [matrix, setMatrix] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("natural");

  useEffect(() => {
    if (!hasAdminSession()) { navigate("/admin/login"); return; }
    api.get("/admin/diamond-matrix").then(({ data }) => {
      setMatrix({
        natural: { ...emptySection(), ...(data.natural || {}) },
        lab: { ...emptySection(), ...(data.lab || {}) },
      });
    }).catch(() => toast.error("Failed to load diamond matrix"));
    // eslint-disable-next-line
  }, []);

  const counts = useMemo(() => {
    if (!matrix) return { natural: 0, lab: 0 };
    const c = (sec) => (sec.sizes?.length || 0) * (sec.grades?.length || 0);
    return { natural: c(matrix.natural), lab: c(matrix.lab) };
  }, [matrix]);

  if (!matrix) {
    return <AdminShell title="Diamond Rates"><div className="py-24 text-center text-muted-foreground">Loading...</div></AdminShell>;
  }

  const sanitize = (sec) => ({
    sizes: (sec.sizes || []).filter((s) => (s.label || "").trim()).map((s) => ({
      id: s.id, label: s.label.trim(),
      carat_min: s.carat_min === "" || s.carat_min == null ? null : Number(s.carat_min),
      carat_max: s.carat_max === "" || s.carat_max == null ? null : Number(s.carat_max),
    })),
    grades: (sec.grades || []).filter((g) => (g.label || "").trim()).map((g) => ({ id: g.id, label: g.label.trim() })),
    rates: Object.fromEntries(Object.entries(sec.rates || {}).map(([k, v]) => [k, Number(v) || 0])),
  });

  const save = async () => {
    // Validation: warn about unnamed rows that will be dropped
    setSaving(true);
    try {
      const payload = { natural: sanitize(matrix.natural), lab: sanitize(matrix.lab) };
      const { data } = await api.put("/admin/diamond-matrix", payload);
      setMatrix({
        natural: { ...emptySection(), ...(data.matrix?.natural || payload.natural) },
        lab: { ...emptySection(), ...(data.matrix?.lab || payload.lab) },
      });
      toast.success("Diamond rates saved — product prices recalculated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminShell title="Diamond Rates">
      <div className="rounded-xl border border-border bg-[#F7F0E6] p-4 mb-5 flex items-start gap-3">
        <Info className="h-4 w-4 text-[hsl(var(--primary))] mt-0.5 shrink-0" />
        <p className="text-sm text-muted-foreground">
          Build a per-carat price table for each diamond <strong>Size × Grade</strong>, kept separately for
          <strong> Natural</strong> and <strong>Lab-Grown</strong>. Products pull their diamond rate automatically from this table.
          Everything here is fully editable — add or rename sizes/grades any time as your business evolves.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-2 w-full max-w-md">
          <TabsTrigger value="natural" data-testid="diamond-tab-natural" className="gap-2"><Gem className="h-4 w-4" /> Natural <Badge variant="secondary" className="ml-1">{counts.natural} cells</Badge></TabsTrigger>
          <TabsTrigger value="lab" data-testid="diamond-tab-lab" className="gap-2"><Sparkles className="h-4 w-4" /> Lab-Grown <Badge variant="secondary" className="ml-1">{counts.lab} cells</Badge></TabsTrigger>
        </TabsList>

        <TabsContent value="natural" className="mt-4">
          <SectionEditor section={matrix.natural} onChange={(s) => setMatrix({ ...matrix, natural: s })} testPrefix="natural" />
        </TabsContent>
        <TabsContent value="lab" className="mt-4">
          <SectionEditor section={matrix.lab} onChange={(s) => setMatrix({ ...matrix, lab: s })} testPrefix="lab" />
        </TabsContent>
      </Tabs>

      <div className="sticky bottom-0 mt-6 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-4 bg-[hsl(var(--background))]/90 backdrop-blur border-t border-border">
        <Button data-testid="diamond-matrix-save-button" onClick={save} disabled={saving} className="gap-2 h-12 w-full sm:w-auto">
          <Save className="h-4 w-4" /> {saving ? "Saving..." : "Save Diamond Rates & Recalculate Prices"}
        </Button>
      </div>
    </AdminShell>
  );
}
